import React, { useRef, useEffect } from 'react';
import PricingSection from './VonScalperPricing';
import FeaturesSection from './VonScalperFeatures';
import TechnicalAdvantageSection from './VonScalperTechnical';
import TestimonialsSection from './VonScalperTestimonials';
import FAQSection from './VonScalperFAQ';

const HonVonScalper = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    // Force document to be scrollable and remove default margins/padding
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    
    const forceScrollableStyles = `
      html, body, #root, .App, .von-scalper-page {
        overflow-y: auto !important;
        height: auto !important;
        min-height: 100% !important;
        position: relative !important;
        overscroll-behavior: auto !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      a, button, .clickable, [role="button"] {
        cursor: pointer !important;
        position: relative !important;
        z-index: 50 !important;
      }
      section {
        position: relative !important;
        z-index: 1 !important;
      }
      .faq-item, .faq-header, .faq-toggle {
        cursor: pointer !important;
        position: relative !important;
        z-index: 50 !important;
      }
      .absolute {
        pointer-events: none !important;
      }
      .absolute a, .absolute button, .absolute [role="button"] {
        pointer-events: auto !important;
      }
  @media (max-width: 768px) {
  body {
    margin: 0 !important;
    padding: 0 !important;
  }
  .von-scalper-page {
    margin-top: 0 !important;
    padding-top: max(env(safe-area-inset-top), 80px) !important;
  }
  .hero-section {
    padding-top: max(env(safe-area-inset-top, 20px), 32px) !important;
    padding-bottom: 32px !important;
    min-height: calc(100vh - env(safe-area-inset-top, 0px)) !important;
  }
  .container {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
  /* Ensure the first text element has proper spacing */
  .hero-section h1 {
    margin-top: 16px !important;
  }
}
    `;
    const styleEl = document.createElement('style');
    styleEl.type = 'text/css';
    styleEl.appendChild(document.createTextNode(forceScrollableStyles));
    document.head.appendChild(styleEl);
  
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
      document.documentElement.style.margin = '';
      document.documentElement.style.padding = '';
      // Safely remove style element
      if (styleEl && styleEl.parentNode) {
        styleEl.parentNode.removeChild(styleEl);
      }
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        containerRef.current.style.display = 'none';
        void containerRef.current.offsetHeight;
        containerRef.current.style.display = '';
      }
    };
    
    setTimeout(handleResize, 500);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div ref={containerRef} className="von-scalper-page" style={{
  overflowY: 'auto',
  overflowX: 'hidden',
  width: '100%',
  height: 'auto',
  minHeight: '100vh',
  margin: '0 !important',
  padding: '0 !important',
  paddingTop: '80px !important', // Space for navbar
  position: 'relative',
  zIndex: 1,
}}>
      {/* Hero Section */}
      <section
  className="hero-section relative bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white py-8 sm:py-16 min-h-screen flex items-center"
>
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-purple-900/90 to-slate-900/95 pointer-events-none"></div>
          {/* Animated particles */}
          <div className="absolute top-20 left-20 w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-32 w-1 h-1 bg-purple-400 rounded-full animate-ping"></div>
          <div className="absolute bottom-32 left-1/4 w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-2 h-2 bg-indigo-400 rounded-full animate-ping"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-cyan-600/20 to-purple-600/20 border border-cyan-500/30 backdrop-blur-sm">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 animate-pulse"></span>
                  <span className="text-cyan-300 text-sm font-medium">PROFESSIONAL TRADING SYSTEM</span>
                </div>
                
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold tracking-tight leading-tight">
                  <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    HON-VONSCALPER
                  </span>
                  <span className="block text-xl sm:text-2xl md:text-3xl lg:text-4xl mt-4 font-semibold text-slate-200">
                    Advanced Multi-Market Scalping Engine
                  </span>
                </h1>
              </div>

              <div className="space-y-6">
                <p className="text-xl lg:text-2xl font-light leading-relaxed text-slate-300 max-w-2xl">
                  Institutional-grade algorithmic scalping technology engineered for consistent performance across 
                  <span className="text-cyan-400 font-medium"> Forex, Crypto, and Synthetic Markets</span>
                </p>

                <div className="flex items-center space-x-2 text-lg text-purple-300">
                  <svg className="w-5 h-5 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="italic">"Where institutional precision meets retail accessibility"</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <a 
                  href="#pricing" 
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('pricing');
                  }} 
                  className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-cyan-600 to-purple-600 text-white font-semibold rounded-xl hover:from-cyan-500 hover:to-purple-500 transition-all duration-300 shadow-lg hover:shadow-cyan-500/25 transform hover:scale-105 relative z-50"
                >
                  <span>Get Started Now</span>
                  <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
                
                <a 
                  href="https://www.myfxbook.com/members/Adiec7/vonscalper/11617779" 
                  className="group inline-flex items-center justify-center px-8 py-4 bg-slate-800/50 text-slate-200 font-semibold rounded-xl border border-slate-600 hover:bg-slate-700/50 hover:border-slate-500 transition-all duration-300 backdrop-blur-sm relative z-50"
                >
                  <span>View Performance</span>
                  <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </a>
              </div>
            </div>

            <div className="relative">
              {/* Main Dashboard Mockup */}
              <div className="relative bg-gradient-to-br from-slate-800 via-slate-900 to-purple-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-700">
                {/* Top Bar */}
                <div className="bg-gradient-to-r from-slate-800 to-purple-800 p-4 border-b border-slate-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <span className="text-slate-300 text-sm font-medium">VON SCALPER PRO</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-green-400 text-xs font-medium">LIVE</span>
                    </div>
                  </div>
                </div>

                {/* Dashboard Content */}
                <div className="p-6 space-y-6">
                  {/* Header Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg p-4 border border-slate-600">
                      <div className="text-cyan-400 text-sm font-medium">Daily P&L</div>
                      <div className="text-2xl font-bold text-white">+$1,247</div>
                      <div className="text-green-400 text-xs">+12.4%</div>
                    </div>
                    <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg p-4 border border-slate-600">
                      <div className="text-purple-400 text-sm font-medium">Active Trades</div>
                      <div className="text-2xl font-bold text-white">3</div>
                      <div className="text-slate-400 text-xs">2 BUY, 1 SELL</div>
                    </div>
                    <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg p-4 border border-slate-600">
                      <div className="text-pink-400 text-sm font-medium">Win Rate</div>
                      <div className="text-2xl font-bold text-white">78%</div>
                      <div className="text-green-400 text-xs">Today: 85%</div>
                    </div>
                  </div>

                  {/* Market Bias */}
                  <div className="bg-gradient-to-r from-slate-700/50 to-purple-700/50 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-slate-300 font-medium">Market Bias</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <span className="text-green-400 text-sm font-medium">BULLISH</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <span className="text-slate-400">EURUSD</span>
                      <span className="text-slate-400">BTCUSD</span>
                      <span className="text-slate-400">V75</span>
                      <span className="text-slate-400">XAUUSD</span>
                    </div>
                  </div>

                  {/* Active Zones */}
                  <div className="space-y-2">
                    <div className="text-slate-300 text-sm font-medium">Active POI Zones: 7</div>
                    <div className="grid grid-cols-2 gap-2">
                      {['FVG Zone', 'Order Block', 'Breaker Block', 'DR Liquidity'].map((zone, idx) => (
                        <div key={idx} className="bg-slate-700/30 rounded px-2 py-1 text-xs text-slate-400">
                          {zone}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br from-cyan-600/20 to-purple-600/20 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full">
            <path fill="#ffffff" fillOpacity="1" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,218.7C672,235,768,245,864,229.3C960,213,1056,171,1152,165.3C1248,160,1344,192,1392,208L1440,224L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </section>
      
      {/* Core Technology Section */}
      <section className="py-24 bg-gradient-to-br from-white via-slate-50 to-purple-50" id="technology">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <div className="h-0.5 w-12 bg-gradient-to-r from-cyan-500 to-purple-500 mr-4"></div>
              <span className="py-2 px-4 rounded-full text-purple-700 bg-purple-100 font-semibold text-sm border border-purple-200">CORE TECHNOLOGY</span>
              <div className="h-0.5 w-12 bg-gradient-to-r from-purple-500 to-cyan-500 ml-4"></div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-slate-800 to-purple-800 bg-clip-text text-transparent">
              Multi-Dimensional Market Intelligence
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Advanced algorithmic framework combining institutional-grade pattern recognition with 
              adaptive machine learning for consistent cross-market performance.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div className="space-y-8">
              <div className="space-y-6">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800">Tri-Timeframe Convergence Engine</h3>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Sophisticated 15M/5M/1M analysis convergence with real-time bias detection, 
                  market structure recognition, and signal confluence validation for precision entry timing.
                </p>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                {[
                  { icon: "🎯", title: "Real-Time Bias Detection", desc: "Dynamic directional filtering with confidence scoring" },
                  { icon: "🔄", title: "Market Structure Recognition", desc: "Advanced ICT pattern validation across timeframes" },
                  { icon: "⚡", title: "Signal Confluence", desc: "Multi-layer confirmation before trade execution" },
                  { icon: "🧠", title: "Adaptive Intelligence", desc: "ML-powered parameter optimization" }
                ].map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start space-x-4 p-4 rounded-xl bg-white shadow-sm border border-slate-200 hover:shadow-md transition-all duration-300"
                  >
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">{item.title}</h4>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-slate-900 to-purple-900 rounded-2xl p-8 text-white shadow-2xl">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xl font-bold">Multi-Timeframe Analysis</h4>
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
                      <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse delay-100"></div>
                      <div className="w-3 h-3 bg-pink-400 rounded-full animate-pulse delay-200"></div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-cyan-300">T1 (15M)</span>
                      <span className="text-green-400 font-semibold">BULLISH ✓</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-purple-300">T2 (5M)</span>
                      <span className="text-green-400 font-semibold">CONFIRMED ✓</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-pink-300">T3 (1M)</span>
                      <span className="text-yellow-400 font-semibold">ENTRY READY</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-slate-700 pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Confluence Score</span>
                      <span className="text-2xl font-bold text-green-400">87%</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-xl"></div>
            </div>
          </div>

          {/* Multi-Market Support */}
          <div className="bg-gradient-to-r from-slate-800 to-purple-800 rounded-2xl p-8 md:p-12 text-white">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6">Universal Asset Compatibility</h3>
                <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                  Intelligent symbol recognition with adaptive parameters for optimal performance across 
                  Forex majors, Cryptocurrency markets, and V75 Synthetic Indices.
                </p>
                
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { name: "FOREX", pairs: "28+ Pairs", icon: "💱" },
                    { name: "CRYPTO", pairs: "24/7 Trading", icon: "₿" },
                    { name: "V75", pairs: "Synthetic", icon: "📊" }
                  ].map((market, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-3xl mb-2">{market.icon}</div>
                      <div className="font-semibold">{market.name}</div>
                      <div className="text-sm text-slate-400">{market.pairs}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  "Auto-Market Detection",
                  "Regime Classification", 
                  "Parameter Adaptation",
                  "Risk Normalization"
                ].map((feature, idx) => (
                  <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                    <div className="font-semibold text-sm">{feature}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <FeaturesSection />
      
      {/* Technical Advantage Section */}
      <TechnicalAdvantageSection />

      {/* Performance Section */}
      <section id="results" className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-600 rounded-full mix-blend-multiply opacity-10 filter blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply opacity-10 filter blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <div className="h-0.5 w-12 bg-gradient-to-r from-cyan-400 to-purple-400 mr-4"></div>
              <span className="py-2 px-4 rounded-full text-cyan-300 bg-cyan-900/60 font-semibold text-sm border border-cyan-700">PERFORMANCE METRICS</span>
              <div className="h-0.5 w-12 bg-gradient-to-r from-purple-400 to-cyan-400 ml-4"></div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Institutional-Grade Results
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Consistent performance across multiple market conditions through advanced risk management 
              and intelligent position sizing protocols.
            </p>
            <p className="text-cyan-400 mt-6 text-sm font-medium">
              *Simulated performance based on backtesting. Trading involves substantial risk of loss.
            </p>
          </div>

          {/* Main Performance Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              { 
                title: "Risk-Adjusted Returns", 
                value: "14.4%", 
                subtitle: "Annual Sharpe: 1.8", 
                color: "from-cyan-600 to-blue-600",
                icon: "📈"
              },
              { 
                title: "Maximum Drawdown", 
                value: "8.2%", 
                subtitle: "Recovery Factor: 4.3", 
                color: "from-purple-600 to-indigo-600",
                icon: "🛡️"
              },
              { 
                title: "Win Rate Consistency", 
                value: "74%", 
                subtitle: "Avg. Trade: 4.2h", 
                color: "from-indigo-600 to-purple-600",
                icon: "🎯"
              },
              { 
                title: "Risk:Reward Ratio", 
                value: "1:2.8", 
                subtitle: "Avg. RR Range", 
                color: "from-purple-600 to-pink-600",
                icon: "⚖️"
              }
            ].map((metric, index) => (
              <div 
                key={index}
                className={`bg-gradient-to-br ${metric.color} rounded-2xl p-6 text-center relative overflow-hidden transform transition hover:scale-105 shadow-xl`}
              >
                <div className="relative z-10">
                  <div className="text-3xl mb-3">{metric.icon}</div>
                  <h3 className="text-white/80 text-sm font-medium mb-2">{metric.title}</h3>
                  <div className="text-4xl font-bold mb-1 text-white">{metric.value}</div>
                  <p className="text-white/70 text-xs">{metric.subtitle}</p>
                </div>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-white/10 rounded-full"></div>
              </div>
            ))}
          </div>

          {/* Detailed Stats */}
          <div className="bg-gradient-to-r from-slate-800/50 to-purple-800/50 rounded-2xl p-8 backdrop-blur-sm border border-slate-700">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              {[
                { label: "Total Signals", value: "3,247", desc: "Across all markets" },
                { label: "Profitable Trades", value: "2,403", desc: "74% success rate" },
                { label: "Avg. Monthly Return", value: "18.6%", desc: "Risk-adjusted" },
                { label: "System Uptime", value: "99.7%", desc: "24/7 monitoring" }
              ].map((stat, index) => (
                <div key={index} className="space-y-2">
                  <div className="text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-cyan-300 font-medium">{stat.label}</div>
                  <div className="text-slate-400 text-sm">{stat.desc}</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Multi-Market Performance */}
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {[
              { market: "Forex Majors", performance: "+15.2%", trades: "1,847", color: "cyan" },
              { market: "Cryptocurrency", performance: "+28.9%", trades: "892", color: "purple" },
              { market: "V75 Synthetic", performance: "+31.7%", trades: "508", color: "pink" }
            ].map((market, idx) => (
              <div key={idx} className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
                <div className={`text-${market.color}-400 font-semibold mb-2`}>{market.market}</div>
                <div className="text-2xl font-bold text-white mb-1">{market.performance}</div>
                <div className="text-slate-400 text-sm">{market.trades} trades analyzed</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Pricing Section */}
      <PricingSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden" id="contact">
        {/* Background Effects */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 right-20 w-64 h-64 bg-cyan-600 rounded-full mix-blend-multiply opacity-20 filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-purple-600 rounded-full mix-blend-multiply opacity-20 filter blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Ready to Scale Your Trading?
                </h2>
                <p className="text-xl md:text-2xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
                  Join professional traders using institutional-grade scalping technology 
                  with proven algorithms and professional risk management.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <a 
                  href="#pricing" 
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('pricing');
                  }} 
                  className="group inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-cyan-600 to-purple-600 text-white font-bold text-lg rounded-2xl hover:from-cyan-500 hover:to-purple-500 transition-all duration-300 shadow-2xl hover:shadow-cyan-500/25 transform hover:scale-105 relative z-50"
                >
                  <span>Start Trading Now</span>
                  <svg className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
                
                <a 
                  href="/contact" 
                  className="group inline-flex items-center justify-center px-10 py-5 bg-slate-800/50 text-slate-200 font-bold text-lg rounded-2xl border-2 border-slate-600 hover:bg-slate-700/50 hover:border-slate-500 transition-all duration-300 backdrop-blur-sm relative z-50"
                >
                  <span>Contact Sales</span>
                  <svg className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center items-center gap-8 mt-12 text-slate-400">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">30-Day Money Back</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">Secure Payment</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span className="text-sm">24/7 Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HonVonScalper;